import { toRaw } from 'vue';

import parent from '@js/dom/parent';

export const ORIGIN = '*';
export const PREFIX = 'credere';

/*
 * The suffix separates the two senses: `<model>:<verb>` is a fact the host handles if it wants,
 * and `<model>:<verb>:action` is a navigation request, the only one carrying `{ action,
 * settings }`. The type is the model's channel, not the destination, which the reader resolves
 * from `action`.
 */
export const TYPES = [
  'scroll',
  'load',
  'resize',
  'token',

  'customer:read',
  'customer:read:action',
  'customer:list:action',
  'customer:create',
  'customer:create:action',
  'customer:update',

  'public_simulator_setting:read',
  'public_simulator_setting:create',
  'public_simulator_setting:update',
  'public_simulator_setting:find',
  'public_simulator_setting:contact',
  'public_simulator_setting:update:action',

  'lead:create',
  'lead:update',

  'simulation:create',

  'simulation:read',
  'simulation:read:processing',
  'simulation:read:action',
  'simulation:list:action',

  'proposal:read',
  'proposal:read:action',
  'proposal:list:action',
  'proposal:create',
  'proposal:create:failure',
  'proposal:update',
  'proposal_attempt:create',
  'proposal_attempt:update',

  'vehicle:read',
  'vehicle:read:action',
  'vehicle:list:action',
  'vehicle:create',
  'vehicle:update',
  'vehicle:import',
  'vehicle:list',
  'vehicle:find',

  'vehicle_model:read',
  'vehicle_model:read:action',
  'vehicle_model:list:action',
  'vehicle_model:create',
  'vehicle_model:update',
  'vehicle_model:list',

  'report:read:action',

  // Only the filter: a row opens through `proposal:read:action` and `simulation:read:action`.
  'report:list:action',

  'bank_commission_rules:read',
  'bank_commission_rules:update',

  'bank_ranking:read',
  'bank_ranking:update',

  'public_simulator_distribution:read',
  'public_simulator_distribution:update',
  'public_simulator_distribution:read:action',

  'public_simulator_connection:update',

  'stock_source:update',
  'marketplace_ad:import',
  'marketplace_ad:adopt',

  'public_simulator_automation:read',
  'public_simulator_automation:read:action',
  'public_simulator_automation:list:action',
  'public_simulator_automation:create',
  'public_simulator_automation:update',
  'public_simulator_automation:delete',

  'public_simulator_notification:read',
  'public_simulator_notification:read:action',
  'public_simulator_notification:list:action',
  'public_simulator_notification:create',
  'public_simulator_notification:update',
  'public_simulator_notification:delete',

  'public_simulator_question:read',
  'public_simulator_question:read:action',
  'public_simulator_question:list:action',
  'public_simulator_question:create',
  'public_simulator_question:update',
  'public_simulator_question:delete',

  'public_simulator_conversation:list:action',

  'crm_contact:read:action',
  'crm_contact:list:action',

  'store:read',
  'store:read:action',
  'store:list:action',

  // Also the tab strip's: the two tabs are pages of the host, not state of this app.
  'user_invitation:read:action',
  'user_invitation:create',
  // Deleting an invitation is settled inside this app, so the host is not told about it.
  'user_invitation:delete',

  'system_alert:read',
  'system_alert:read:action',
  'system_alert:list:action',
  'system_alert:create',
  'system_alert:update',

  // The three commercial collections. The price table has no `:update` because its API
  // creates a new one and deactivates the previous of the pair - saving is how it updates.
  'financing_return_rule:read',
  'financing_return_rule:read:action',
  'financing_return_rule:list:action',
  'financing_return_rule:create',
  'financing_return_rule:update',
  'vehicle_price:read',
  'vehicle_price:read:action',
  'vehicle_price:list:action',
  'vehicle_price:create',
  'financing_product_rule:read',
  'financing_product_rule:read:action',
  'financing_product_rule:list:action',
  'financing_product_rule:create',
  'financing_product_rule:update',

  // The option picked in a `/settings` menu: a single type, because the destination is not a
  // model's, and `settings` carries the item key for the host to resolve.
  'setting:read:action',

  // Only the filter: the row opens its origin through `proposal:read:action` and
  // `simulation:read:action`, the proposals and simulations lists' own channels.
  'denied_rescue:list:action',

  'onboarding:subscription:action',
].reduce((reduced, type) => {
  reduced[type] = {
    prefixed: `${PREFIX}:${type}`,
    constant: type.replace(/:/g, '_').toUpperCase(),
  };

  return reduced;
}, {});

/*
 * The payload leaves reactivity before crossing: the structured clone of `postMessage` does not
 * clone a `Proxy`, and the message never goes out. It walks level by level because the proxy is
 * usually inside an object literal built by the screen; only object literals and arrays are
 * rebuilt, so `Date`, `File` and `Blob` pass through whole.
 */
function raw(value) {
  const unwrapped = toRaw(value);

  if (Array.isArray(unwrapped)) {
    return unwrapped.map(raw);
  }

  if (unwrapped?.constructor === Object) {
    return Object.keys(unwrapped).reduce((reduced, key) => {
      reduced[key] = raw(unwrapped[key]);

      return reduced;
    }, {});
  }

  return unwrapped;
}

// The host declares in `CREDERE.listening` the types it listens to, prefixed or not, and the
// question comes in by the constant name. A type that does not exist answers `false`.
const CONSTANTS = Object.keys(TYPES).reduce((reduced, type) => {
  reduced[TYPES[type].constant] = type;

  return reduced;
}, {});

export function listened(constant) {
  const type = CONSTANTS[constant];

  if (!type) {
    return false;
  }

  const listening = window.CREDERE.listening || [];

  return listening.includes(type) || listening.includes(TYPES[type].prefixed);
}

function message(type, payload = {}) {
  if (!TYPES[type]) {
    // eslint-disable-next-line no-console
    console.error(`MESSAGE TYPE ${type} NOT FOUND`);
  }

  parent().postMessage({
    type: TYPES[type].prefixed,
    payload: raw(payload),
  }, ORIGIN);
}

export default Object.keys(TYPES).reduce((reduced, type) => {
  reduced[TYPES[type].constant] = (payload) => message(type, payload);

  return reduced;
}, {});
